// ---------------------------------------------------------------------------
// ActiveFilterChips -- Leaf Component
// ---------------------------------------------------------------------------
// Displays active filter chips with toggle (on/off) and remove (delete).
// - Click chip body: toggle enabled/disabled (filter stays but is not applied)
// - Click X: permanently removes the filter
// - Enabled: solid bg, colored dot indicator, normal text
// - Disabled: transparent bg, dashed border, hollow dot, muted text
// ---------------------------------------------------------------------------

import React from 'react';
import { cn } from '@/lib/utils';
import { X, Search } from 'lucide-react';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type { FilterChip } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActiveFilterChipsProps {
  readonly filters: readonly FilterChip[];
  readonly onRemoveFilter: (filterId: string) => void;
  readonly onToggleFilter?: (filterId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ActiveFilterChips({ filters, onRemoveFilter, onToggleFilter }: ActiveFilterChipsProps) {
  if (filters.length === 0) {
    return null;
  }

  return (
    <>
      {filters.map(filter => {
        const isSearchChip = filter.id === '__search__';
        const isEnabled = filter.enabled !== false;

        return (
          <div
            key={filter.id}
            data-testid={`filter-chip-${filter.id}`}
            className={cn(
              'rounded-md pl-2.5 pr-1.5 py-1.5 flex items-center gap-2 text-xs font-medium transition-all',
              isSearchChip
                ? 'bg-primary/10 border border-primary/30 text-primary'
                : isEnabled
                  ? 'bg-v11-surface border border-v11-border hover:border-primary/50 text-v11-text-secondary hover:bg-v11-surface-hover shadow-sm'
                  : 'bg-v11-surface/30 border border-dashed border-v11-border/50 text-v11-text-muted hover:border-v11-border hover:bg-v11-surface/50'
            )}
          >
            {/* Toggle area: dot indicator + label */}
            {isSearchChip ? (
              <>
                <Search className="h-3 w-3" />
                <span>{filter.value}</span>
              </>
            ) : (
              <button
                type="button"
                onClick={() => onToggleFilter?.(filter.id)}
                className="flex items-center gap-2 transition-opacity cursor-pointer"
                aria-label={
                  isEnabled ? `Disable ${filter.value} filter` : `Enable ${filter.value} filter`
                }
              >
                {/* Mini toggle switch */}
                <span
                  className={cn(
                    'relative inline-flex h-3.5 w-6 shrink-0 rounded-full transition-colors duration-200',
                    isEnabled ? 'bg-primary' : 'bg-v11-text-muted/40'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-[3px] h-2 w-2 rounded-full bg-white shadow-sm transition-transform duration-200',
                      isEnabled ? 'translate-x-3' : 'translate-x-0.5'
                    )}
                  />
                </span>
                {/* Platform icon before label (for platform and channel filters) */}
                {(() => {
                  const platformName = filter.category === 'platform'
                    ? filter.value
                    : filter.category === 'channel' && filter.platform
                      ? filter.platform
                      : null;
                  if (!platformName) return null;
                  const pi = getPlatformIcon(platformName);
                  return pi ? (
                    <span
                      className="[&>svg]:h-3.5 [&>svg]:w-3.5 flex-shrink-0"
                      style={{ color: pi.color }}
                    >
                      {pi.icon}
                    </span>
                  ) : null;
                })()}
                <span>{filter.value}</span>
              </button>
            )}

            {/* Delete button */}
            <button
              data-testid={`filter-chip-remove-${filter.id}`}
              type="button"
              onClick={() => onRemoveFilter(filter.id)}
              className={cn(
                'ml-0.5 rounded-full p-0.5 transition-colors',
                isSearchChip
                  ? 'text-primary/70 hover:text-primary hover:bg-primary/10'
                  : 'text-v11-text-muted hover:text-v11-text-primary hover:bg-v11-surface-hover'
              )}
              aria-label={`Remove ${filter.value} filter`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </>
  );
}

export { ActiveFilterChips };
export type { ActiveFilterChipsProps };
